import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import {
  ClientType,
  CorporateClientDetails,
  IndividualClientDetails,
  PaymentMethod,
} from '@/types';
import { TService } from './services';

export interface SelectedService {
  id: number;
  serviceId: number;
  name: string;
  price: number;
  currency: string;
  quantity: number;
  children: Omit<TService, 'children'>[];
}

export interface ManualInvoiceState {
  // Invoice settings
  paymentMethod: PaymentMethod;
  invoiceCurrency: 'EUR' | 'USD';
  invoicingCompanyId: number | null;

  // Recipient
  recipientType: ClientType;
  individualDetails: IndividualClientDetails;
  corporateDetails: CorporateClientDetails;

  // Services (ordered)
  services: SelectedService[];

  // Actions
  setPaymentMethod: (method: PaymentMethod) => void;
  setCurrency: (currency: 'EUR' | 'USD') => void;
  setInvoicingCompany: (id: number | null) => void;
  setRecipientType: (type: ClientType) => void;
  updateIndividualField: <K extends keyof IndividualClientDetails>(
    field: K,
    value: IndividualClientDetails[K],
  ) => void;
  updateCorporateField: <K extends keyof CorporateClientDetails>(
    field: K,
    value: CorporateClientDetails[K],
  ) => void;
  addService: (service: TService) => void;
  removeService: (serviceId: number) => void;
  updateServiceQuantity: (serviceId: number, quantity: number) => void;
  reorderServices: (fromIndex: number, toIndex: number) => void;
  reset: () => void;
}

const initialIndividualDetails: IndividualClientDetails = {
  fullName: '',
  documentNumber: '',
  nationality: '',
  residenceCountry: '',
  legalAddress: '',
};

const initialCorporateDetails: CorporateClientDetails = {
  companyName: '',
  companyRegNumber: '',
  companyTaxId: '',
  companyLegalAddress: '',
};

const initialState = {
  paymentMethod: PaymentMethod.BankTransfer,
  invoiceCurrency: 'EUR' as const,
  invoicingCompanyId: null,
  recipientType: ClientType.Individual,
  individualDetails: { ...initialIndividualDetails },
  corporateDetails: { ...initialCorporateDetails },
  services: [],
};

export const useManualInvoiceStore = create<ManualInvoiceState>((set) => ({
  ...initialState,

  setPaymentMethod: (method) =>
    set((state) => ({
      paymentMethod: method,
      // When switching to crypto, lock currency to USD
      invoiceCurrency:
        method === PaymentMethod.Cryptocurrency ? 'USD' : state.invoiceCurrency,
    })),

  setCurrency: (currency) =>
    set((state) => ({
      // Don't allow currency change if payment method is crypto
      invoiceCurrency:
        state.paymentMethod === PaymentMethod.Cryptocurrency ? 'USD' : currency,
    })),

  setInvoicingCompany: (id) => set({ invoicingCompanyId: id }),

  setRecipientType: (type) => set({ recipientType: type }),

  updateIndividualField: (field, value) =>
    set((state) => ({
      individualDetails: { ...state.individualDetails, [field]: value },
    })),

  updateCorporateField: (field, value) =>
    set((state) => ({
      corporateDetails: { ...state.corporateDetails, [field]: value },
    })),

  addService: (service) =>
    set((state) => {
      // Check if service already exists
      if (state.services.some((s) => s.serviceId === service.id)) {
        return state;
      }
      const newService: SelectedService = {
        id: Date.now(), // Unique ID for drag-and-drop
        serviceId: service.id,
        name: service.name,
        price: service.price,
        currency: service.currency,
        quantity: 1,
        children: service.children,
      };
      return { services: [...state.services, newService] };
    }),

  removeService: (serviceId) =>
    set((state) => ({
      services: state.services.filter((s) => s.serviceId !== serviceId),
    })),

  updateServiceQuantity: (serviceId, quantity) =>
    set((state) => ({
      services: state.services.map((s) =>
        s.serviceId === serviceId
          ? { ...s, quantity: Math.max(1, quantity) }
          : s,
      ),
    })),

  reorderServices: (fromIndex, toIndex) =>
    set((state) => {
      const newServices = [...state.services];
      const [movedItem] = newServices.splice(fromIndex, 1);
      newServices.splice(toIndex, 0, movedItem);
      return { services: newServices };
    }),

  reset: () => set({ ...initialState }),
}));

// Selector hooks for preventing unnecessary re-renders
export const usePaymentMethod = () =>
  useManualInvoiceStore((state) => state.paymentMethod);
export const useInvoiceCurrency = () =>
  useManualInvoiceStore((state) => state.invoiceCurrency);
export const useInvoicingCompanyId = () =>
  useManualInvoiceStore((state) => state.invoicingCompanyId);
export const useRecipientType = () =>
  useManualInvoiceStore((state) => state.recipientType);
export const useIndividualDetails = () =>
  useManualInvoiceStore((state) => state.individualDetails);
export const useCorporateDetails = () =>
  useManualInvoiceStore((state) => state.corporateDetails);
export const useSelectedServices = () =>
  useManualInvoiceStore((state) => state.services);

export const useManualInvoiceActions = () =>
  useManualInvoiceStore(
    useShallow((state) => ({
      setPaymentMethod: state.setPaymentMethod,
      setCurrency: state.setCurrency,
      setInvoicingCompany: state.setInvoicingCompany,
      setRecipientType: state.setRecipientType,
      updateIndividualField: state.updateIndividualField,
      updateCorporateField: state.updateCorporateField,
      addService: state.addService,
      removeService: state.removeService,
      updateServiceQuantity: state.updateServiceQuantity,
      reorderServices: state.reorderServices,
      reset: state.reset,
    })),
  );

// Helper to get recipient country based on type
export const useRecipientCountry = () =>
  useManualInvoiceStore(
    (state) =>
      state.recipientType === ClientType.Individual
        ? state.individualDetails.residenceCountry
        : '', // Corporate doesn't have country in current types
  );
